import { useLocation, useNavigate } from 'react-router-dom';
import { productApiClient } from '../clients/ProductAPIClient';
import { AlbumState } from '../data/models/albumState';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';

export type StateTransitionContext = {
  albumId: string;
  changeRequestId?: string;
  albumTitle: string;
  newState: AlbumState;
  returnToState?: boolean;
};

/**
 * Hook to handle product state transitions.
 */
export default function useProductStateTransition(submissionsGridUrl: string) {
  const productState = useProductStateTransitionContext();
  const clearProductState = useClearProductStateTransitionContext();
  const updateProductState = useUpdateProductState(submissionsGridUrl);
  const updateChangeRequestState =
    useUpdateChangeRequestState(submissionsGridUrl);
  return {
    productState,
    clearProductState,
    updateProductState,
    updateChangeRequestState,
  };
}

/**
 * Hook to retrieve information about the last product state transition.
 *
 *  _Data is stored in the window location state so that it can be retrieved after navigation._
 */
export function useProductStateTransitionContext() {
  const { state } = useLocation();

  if (
    !state?.stateTransitionAlbumId ||
    !state?.stateTransitionAlbumTitle ||
    !state?.stateTransitionNewState
  ) {
    return null;
  }

  const context: StateTransitionContext = {
    albumId: state.stateTransitionAlbumId,
    changeRequestId: state.stateTransitionChangeRequestId,
    albumTitle: state.stateTransitionAlbumTitle,
    newState: state.stateTransitionNewState,
    returnToState: state.stateTransitionReturnToState,
  };

  return context;
}

/**
 * Hook to clear product state transition data stored in the window location state.
 */
export function useClearProductStateTransitionContext() {
  const navigate = useNavigate();
  return () => navigate(location.pathname, { replace: true, state: null });
}

/**
 * Updates the state of a product and handles navigation back to the product grid.
 *
 * _Information about the state transition is stored in the window location state to allow is to be retrieved later (after page navigation)._
 */
export function useUpdateProductState(submissionsGridUrl: string) {
  const navigate = useNavigate();

  return async ({
    albumId,
    albumTitle,
    newState,
    notes,
    returnToState,
  }: {
    albumId: string;
    albumTitle: string;
    newState: AlbumState;
    notes?: string;
    returnToState?: boolean;
  }) => {
    const response = await productApiClient.updateProductState(
      albumId,
      newState,
      notes
    );
    const stateTransitionNewState = response.data?.albumState ?? newState;

    navigate(submissionsGridUrl, {
      state: {
        stateTransitionAlbumId: albumId,
        stateTransitionAlbumTitle: albumTitle,
        stateTransitionNewState,
        stateTransitionReturnToState: returnToState,
      },
    });
  };
}

export function useUpdateChangeRequestState(releaseGridUrl: string) {
  const navigate = useNavigate();

  return async ({
    albumId,
    changeRequestId,
    albumTitle,
    newState,
    nonce,
    returnToState,
  }: {
    albumId: string;
    changeRequestId: string;
    albumTitle: string;
    newState: AlbumState;
    nonce: string;
    returnToState?: boolean;
  }) => {
    await changeRequestApiClient.updateChangeRequestState(
      albumId,
      changeRequestId,
      newState,
      nonce
    );

    navigate(releaseGridUrl, {
      state: {
        stateTransitionAlbumId: albumId,
        stateTransitionChangeRequestId: changeRequestId,
        stateTransitionAlbumTitle: albumTitle,
        stateTransitionNewState: newState,
        stateTransitionReturnToState: returnToState,
      },
    });
  };
}
